import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarBlank, ChartLineUp, Clock, MagnifyingGlass, Sparkle, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { fmtDate, scoreClass, scoreLabel, shortUrl } from "@/lib/score-utils";

export default function DashboardPage() {
  const [audits, setAudits] = useState([]);
  const [schedules, setSchedules] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [a, s] = await Promise.all([
        api.get("/audits"),
        api.get("/schedules").catch(() => ({ data: null })),
      ]);
      setAudits(a.data.audits || []);
      setSchedules(s.data);
    } catch (err) {
      toast.error("Failed to load audits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const remove = async (id) => {
    if (!confirm("Delete this audit?")) return;
    await api.delete(`/audits/${id}`);
    toast.success("Audit deleted");
    load();
  };

  const stats = computeStats(audits);

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-10 gap-6 flex-wrap">
        <div>
          <div className="label-overline mb-2">Dashboard</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none">
            Your audits
          </h1>
        </div>
        <Link
          to="/app/new"
          data-testid="dashboard-new-audit-button"
          className="inline-flex items-center gap-2 bg-ink text-white px-5 py-3 rounded-sm font-semibold hover:bg-brand transition-all hard-shadow-sm hover:-translate-y-0.5"
        >
          <MagnifyingGlass size={16} /> New audit
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Total audits" value={stats.total} icon={ChartLineUp} testId="stat-total" />
        <StatCard label="Average score" value={stats.avg ?? "—"} icon={Sparkle} cls={scoreClass(stats.avg)} testId="stat-avg" />
        <StatCard label="Running" value={stats.running} icon={Clock} testId="stat-running" />
        <StatCard label="Last 7 days" value={stats.recent} icon={ArrowUpRight} testId="stat-recent" />
      </div>

      <WatchlistStrip schedules={schedules} audits={audits} />

      <div className="surface-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="label-overline">History</div>
            <div className="text-sm text-ink-muted">All audits, newest first</div>
          </div>
          <span className="mono text-xs text-ink-muted" data-testid="audits-count">
            {audits.length} {audits.length === 1 ? "AUDIT" : "AUDITS"}
          </span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-ink-muted" data-testid="audits-loading">Loading…</div>
        ) : audits.length === 0 ? (
          <div className="p-12 text-center" data-testid="audits-empty">
            <div className="mx-auto w-12 h-12 border-2 border-ink rounded-sm flex items-center justify-center mb-4">
              <MagnifyingGlass size={20} />
            </div>
            <div className="font-display text-xl font-bold mb-2">No audits yet</div>
            <p className="text-sm text-ink-muted mb-6">Drop a URL and we&apos;ll run sixty checks in seconds.</p>
            <Link to="/app/new" className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2.5 rounded-sm font-semibold hover:bg-brand transition-colors"
              data-testid="empty-state-new-audit">
              Run first audit <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audits-table">
              <thead className="bg-secondary border-b border-border">
                <tr className="text-left label-overline">
                  <th className="px-6 py-3 font-semibold">URL</th>
                  <th className="px-4 py-3 font-semibold w-24">Status</th>
                  <th className="px-4 py-3 font-semibold w-20 text-right">Overall</th>
                  <th className="px-4 py-3 font-semibold w-16 text-right">SEO</th>
                  <th className="px-4 py-3 font-semibold w-16 text-right">Perf</th>
                  <th className="px-4 py-3 font-semibold w-16 text-right">Sec</th>
                  <th className="px-4 py-3 font-semibold w-16 text-right">A11y</th>
                  <th className="px-4 py-3 font-semibold w-40">Created</th>
                  <th className="px-4 py-3 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {audits.map((a) => (
                  <tr key={a.id} data-testid={`audit-row-${a.id}`}
                    className="border-b border-border last:border-0 hover:bg-secondary cursor-pointer transition-colors"
                    onClick={() => nav(`/app/audit/${a.id}`)}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-ink truncate max-w-[280px]">{shortUrl(a.url)}</div>
                      <div className="text-xs text-ink-muted mono truncate max-w-[280px]">{a.url}</div>
                    </td>
                    <td className="px-4 py-4">
                      <StatusPill status={a.status} />
                    </td>
                    <td className={`px-4 py-4 text-right num-display text-2xl ${scoreClass(a.scores?.overall)}`}>
                      {a.scores?.overall ?? "—"}
                    </td>
                    <td className={`px-4 py-4 text-right mono text-sm ${scoreClass(a.scores?.seo)}`}>{a.scores?.seo ?? "—"}</td>
                    <td className={`px-4 py-4 text-right mono text-sm ${scoreClass(a.scores?.performance)}`}>{a.scores?.performance ?? "—"}</td>
                    <td className={`px-4 py-4 text-right mono text-sm ${scoreClass(a.scores?.security)}`}>{a.scores?.security ?? "—"}</td>
                    <td className={`px-4 py-4 text-right mono text-sm ${scoreClass(a.scores?.accessibility)}`}>{a.scores?.accessibility ?? "—"}</td>
                    <td className="px-4 py-4 mono text-xs text-ink-muted whitespace-nowrap">{fmtDate(a.created_at)}</td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); remove(a.id); }}
                        data-testid={`delete-audit-${a.id}`}
                        className="p-1.5 text-ink-muted hover:text-critical transition-colors"
                      >
                        <TrashSimple size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function StatusPill({ status }) {
  const map = {
    complete: { cls: "border-success text-success bg-success/5", label: "COMPLETE" },
    running: { cls: "border-warning text-warning bg-warning/5", label: "RUNNING" },
    queued: { cls: "border-ink-muted text-ink-muted bg-secondary", label: "QUEUED" },
    failed: { cls: "border-critical text-critical bg-critical/5", label: "FAILED" },
  };
  const s = map[status] || { cls: "border-ink-muted text-ink-muted", label: status?.toUpperCase() || "—" };
  return (
    <span className={`mono text-[10px] tracking-wider px-2 py-1 border rounded-sm font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, cls = "", testId }) {
  return (
    <div className="surface-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-overline">{label}</div>
        <Icon size={18} className="text-ink-muted" />
      </div>
      <div className={`num-display text-4xl ${cls}`}>{value}</div>
    </div>
  );
}

function computeStats(audits) {
  const total = audits.length;
  const completed = audits.filter((a) => a.scores?.overall != null);
  const avg = completed.length
    ? Math.round(completed.reduce((s, a) => s + a.scores.overall, 0) / completed.length)
    : null;
  const running = audits.filter((a) => a.status === "running" || a.status === "queued").length;
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = audits.filter((a) => new Date(a.created_at).getTime() > weekAgo).length;
  return { total, avg, running, recent };
}

/* ------------------------------------------------------------------ */
/* Watchlist — Agency-only mini-cards showing each scheduled URL's    */
/* most recent score + trend vs. previous run.                        */
/* ------------------------------------------------------------------ */
function WatchlistStrip({ schedules, audits }) {
  // Hide entirely if user is not on Agency (server returns allowed=false)
  // or if they haven't added any schedules yet.
  if (!schedules || !schedules.allowed) return null;
  const list = schedules.schedules || [];
  if (list.length === 0) return null;

  return (
    <div className="mb-10" data-testid="watchlist-strip">
      <div className="flex items-baseline justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} className="text-brand" weight="fill" />
          <div className="label-overline">Watchlist</div>
          <span className="mono text-xs text-ink-muted">{list.length} URL{list.length === 1 ? "" : "s"}</span>
        </div>
        <Link to="/app/workspace" className="text-xs text-ink-muted hover:text-ink inline-flex items-center gap-1">
          Manage <ArrowRight size={12} />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.slice(0, 6).map((s) => (
          <WatchlistCard key={s.id} sched={s} audits={audits} />
        ))}
      </div>
    </div>
  );
}

function WatchlistCard({ sched, audits }) {
  // Look up the last 4 completed audits for this URL (most recent first)
  // to render the sparkline + delta arrow.
  const history = audits
    .filter((a) => a.url === sched.url && a.scores?.overall != null)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 4);
  const cur = history[0]?.scores?.overall ?? sched.last_score ?? null;
  const prev = history[1]?.scores?.overall ?? null;
  const delta = cur != null && prev != null ? cur - prev : null;
  const arrow = delta == null ? "—" : delta > 0 ? "↑" : delta < 0 ? "↓" : "·";
  const deltaCls = delta == null ? "text-ink-muted" : delta > 0 ? "text-success" : delta < 0 ? "text-critical" : "text-ink-muted";

  const last = history[0]?.created_at || sched.last_run_at;

  return (
    <Link
      to={history[0] ? `/app/audit/${history[0].id}` : "/app/workspace"}
      className="surface-card p-4 hover:border-ink transition-colors block"
      data-testid={`watchlist-${sched.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="mono text-xs truncate text-ink" title={sched.url}>
            {shortUrl(sched.url)}
          </div>
          <div className="text-[10px] mono text-ink-muted tracking-wider mt-0.5 uppercase">
            {sched.frequency} · {sched.active ? "active" : "paused"}
          </div>
        </div>
        <div className={`num-display text-3xl font-black tabular-nums leading-none ${scoreClass(cur)}`}>
          {cur ?? "—"}
        </div>
      </div>

      {/* Sparkline — last up-to-4 runs. Reversed so left = oldest, right = newest. */}
      <Sparkline values={history.map((h) => h.scores.overall).reverse()} />

      <div className="flex items-baseline justify-between text-[11px] mono mt-2">
        <span className="text-ink-muted">
          {last ? `Last run · ${relTime(last)}` : "No runs yet"}
        </span>
        <span className={`font-bold ${deltaCls}`}>
          {prev != null ? `${prev} ${arrow} ${cur ?? "—"}` : arrow}
        </span>
      </div>
    </Link>
  );
}

/**
 * Sparkline — tiny inline line chart of recent scores. If we have fewer
 * than 2 points there's nothing meaningful to draw, so we render a
 * subtle dashed placeholder instead.
 */
function Sparkline({ values, width = 220, height = 28 }) {
  if (!values || values.length < 2) {
    return (
      <div className="h-7 border-t border-dashed border-border flex items-center justify-center">
        <span className="text-[9px] mono text-ink-muted tracking-wider">building history…</span>
      </div>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / span) * (height - 6) - 3;
    return [x, y];
  });
  const last = values[values.length - 1];
  const first = values[0];
  const trendUp = last >= first;
  const color = trendUp ? "#16A34A" : "#DC2626";
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const area = `${path} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height: `${height}px` }}
      aria-hidden="true"
      data-testid="watchlist-sparkline"
    >
      <path d={area} fill={color} opacity="0.10" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 2.4 : 1.6}
          fill={i === pts.length - 1 ? color : "#9BA0A8"} />
      ))}
    </svg>
  );
}

// Short relative time helper — "2 days ago" / "3 hr ago" / "just now"
function relTime(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  const mo = Math.floor(d / 30);
  return `${mo} mo ago`;
}
